"use client";

import { useEffect } from "react";
import Script from "next/script";
import { site } from "@/content/site";
import { useCookieConsent } from "@/lib/use-cookie-consent";

/** Cookie impostati da GA4: `_ga` e `_ga_<ID proprietà senza "G-">`. Rimossi
 * appena il consenso passa a "rejected", per chi accetta e poi cambia idea.
 * `_gid` è di Universal Analytics (non più usato) ma si cancella per sicurezza. */
function clearGaCookies(measurementId: string): void {
  const expired = "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
  const names = ["_ga", "_gid", `_ga_${measurementId.replace(/^G-/, "")}`];
  for (const name of names) document.cookie = `${name}${expired}`;
}

/**
 * Carica gtag.js SOLO dopo consenso esplicito (consent === "accepted") e solo
 * se un measurement ID è configurato (env o `site.gaMeasurementId`). Mai nel
 * `<head>` statico: prima del consenso non parte nessuna richiesta verso
 * Google e non viene scritto nessun cookie. Se la scelta passa a "rejected"
 * dopo un "accepted", la raccolta viene disattivata subito (`ga-disable-<ID>`,
 * meccanismo ufficiale di Google) e i cookie vengono cancellati. Vedi decisions.md.
 */
export function GoogleAnalytics() {
  const consent = useCookieConsent();
  const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID ?? site.gaMeasurementId;

  useEffect(() => {
    if (!measurementId) return;
    const flag = `ga-disable-${measurementId}` as const;
    if (consent === "accepted") {
      Reflect.deleteProperty(window, flag);
      return;
    }
    // Nessuna scelta o rifiuto: raccolta disattivata e cookie GA assenti.
    Reflect.set(window, flag, true);
    if (consent === "rejected") clearGaCookies(measurementId);
  }, [consent, measurementId]);

  if (consent !== "accepted" || !measurementId) return null;

  return (
    <>
      <Script src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`} strategy="afterInteractive" />
      <Script id="ga4-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${measurementId}');
        `}
      </Script>
    </>
  );
}
